import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import { ReactComponent as SeeMoreIcon } from '../../images/SeeMoreArrow.svg';
import ContentRow from '../ContentRow';
import GlobalSearchStyles from '../styles';

const COLLAPSED_COUNT = 4;

const WrapperDiv = styled.div`
  margin-bottom: 32px;
`;

const CollapsibleHeaderRow = styled(GlobalSearchStyles.GridHeaderRow)`
  gap: 8px;
  cursor: pointer;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

const SeeMoreRow = styled(Row)`
  padding: 14px 24px;
  &:hover {
    background-color: var(--color-bg-hover);
    cursor: pointer;
  }
`;

const ContentResultGroup = ({
  group,
  searchText,
  onContentNavigate,
  isCollapsed,
  onToggleCollapsed,
}: {
  group: GlobalSearch.ContentResultGroup;
  searchText: string;
  onContentNavigate: (result: GlobalSearch.ContentResult) => void;
  isCollapsed: boolean;
  onToggleCollapsed: (type: GlobalSearch.ContentResultType) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const itemsToRender = isExpanded
    ? group.results
    : group.results.slice(0, COLLAPSED_COUNT);
  const isMore = group.results.length > itemsToRender.length;

  const onShowMoreClicked = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const onHeaderClicked = useCallback(() => {
    onToggleCollapsed(group.type);
  }, [group.type, onToggleCollapsed]);

  const onHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleCollapsed(group.type);
      }
    },
    [group.type, onToggleCollapsed],
  );

  return (
    <WrapperDiv data-testid={`content-results-${group.type}`}>
      <CollapsibleHeaderRow
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        onClick={onHeaderClicked}
        onKeyDown={onHeaderKeyDown}
        data-testid={`content-results-header-${group.type}`}
      >
        <Icon
          icon={isCollapsed ? 'chevron-right' : 'chevron-down'}
          width={16}
          height={16}
          color={'var(--color-text-muted)'}
        />
        <GlobalSearchStyles.Col>
          <Typography.Header type="H5">{`${group.label} (${group.results.length})`}</Typography.Header>
        </GlobalSearchStyles.Col>
      </CollapsibleHeaderRow>
      {!isCollapsed && (
        <>
          {itemsToRender.map((result) => (
            <ContentRow
              key={result.id}
              result={result}
              searchText={searchText}
              onContentNavigate={onContentNavigate}
            />
          ))}
          {isMore && (
            <SeeMoreRow centerAlign onClick={onShowMoreClicked}>
              <div style={{ marginRight: 10 }}>
                <Typography.Body type="Link">{`View More ${group.label}`}</Typography.Body>
              </div>
              <SeeMoreIcon />
            </SeeMoreRow>
          )}
        </>
      )}
    </WrapperDiv>
  );
};

const ContentResults = ({
  groups,
  searchText,
  onContentNavigate,
}: {
  groups: GlobalSearch.ContentResultGroup[];
  searchText: string;
  onContentNavigate: (result: GlobalSearch.ContentResult) => void;
}) => {
  // Held here rather than per group so a collapse survives further typing
  const [collapsedTypes, setCollapsedTypes] = useState<
    GlobalSearch.ContentResultType[]
  >([]);

  const onToggleCollapsed = useCallback(
    (type: GlobalSearch.ContentResultType) => {
      setCollapsedTypes((current) =>
        current.includes(type)
          ? current.filter((t) => t !== type)
          : [...current, type],
      );
    },
    [],
  );

  return (
    <>
      {groups.map((group) => (
        <ContentResultGroup
          // Re-keyed per search so "View More" collapses again
          key={`${group.type}-${searchText}`}
          group={group}
          searchText={searchText}
          onContentNavigate={onContentNavigate}
          isCollapsed={collapsedTypes.includes(group.type)}
          onToggleCollapsed={onToggleCollapsed}
        />
      ))}
    </>
  );
};

export default ContentResults;
